/**
 * CDEK API client — typed contract between frontend and backend.
 * Ported from website-repo/src/features/checkout/api/cdek.api.ts
 * Backend: backend-repo/src/api/routes/cdek.routes.ts
 */

import type { ProductType } from '@/shared/lib/product-type';

// ============================================================================
// Domain types
// ============================================================================

export interface CdekCity {
  code: number;
  city: string;
  region: string;
}

export interface PickupPoint {
  code: string;
  name: string;
  type: "PVZ" | "POSTAMAT";
  cityCode: number;
  city: string;
  region: string | null;
  address: string;
  fullAddress: string;
  addressComment: string | null;
  workTime: string;
  latitude: number;
  longitude: number;
  hasCash: boolean;
  hasCard: boolean;
  allowedCod: boolean;
  hasDressingRoom: boolean;
}

export interface PickupPointBounds {
  south: number;
  west: number;
  north: number;
  east: number;
}

export interface TariffOption {
  tariffCode: number;
  tariffName: string;
  deliveryCost: number;
  periodMin: number;
  periodMax: number;
  deliveryMode: number;
}

export interface TariffsGroup {
  all: TariffOption[];
  pvz: TariffOption | null;
  door: TariffOption | null;
}

export interface CartItemForApi {
  productType: ProductType;
  productId: string;
  quantity: number;
  weight?: number;
}

// ============================================================================
// Backend response shapes
// ============================================================================

interface PickupPointsByBoundsResponse {
  success: boolean;
  points: PickupPoint[];
  count: number;
  total: number;
  bounds: PickupPointBounds;
  message?: string;
  error?: string;
}

interface TariffsResponse {
  success: boolean;
  tariffs: TariffsGroup;
}

interface CitiesResponse {
  cities: CdekCity[];
}

// ============================================================================
// Fetch functions (AbortSignal passed through for React Query cancellation)
// ============================================================================

export async function fetchPickupPointsByBounds(
  bounds: PickupPointBounds,
  signal?: AbortSignal,
): Promise<PickupPoint[]> {
  const params = new URLSearchParams({
    south: String(bounds.south),
    west: String(bounds.west),
    north: String(bounds.north),
    east: String(bounds.east),
    type: "all",
  });

  const response = await fetch(`/api/cdek/pickup-points/bounds?${params.toString()}`, { signal });

  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { message?: string; error?: string };
    throw new Error(body.message || body.error || `CDEK pickup-points/bounds ${response.status}`);
  }

  const data = await response.json() as PickupPointsByBoundsResponse;
  if (!data.success) throw new Error(data.message || data.error || "CDEK pickup-points/bounds: success false");

  return data.points ?? [];
}

export async function fetchTariffs(
  cityCode: number,
  items: CartItemForApi[],
  signal?: AbortSignal,
): Promise<TariffsGroup | null> {
  const response = await fetch("/api/cdek/calculate-real", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ toCityCode: cityCode, items }),
    signal,
  });

  if (!response.ok) return null;

  const data = await response.json() as TariffsResponse;
  return data.tariffs ?? null;
}

export async function fetchCitiesByName(
  query: string,
  limit = 5,
  signal?: AbortSignal,
  postalCode?: string | null,
  region?: string | null,
): Promise<CdekCity[]> {
  const params = new URLSearchParams({ q: query, limit: String(limit) });
  if (postalCode) params.set("postal_code", postalCode.replace(/\D/g, ""));
  if (region) params.set("region", region);

  const response = await fetch(`/api/cdek/cities?${params.toString()}`, { signal });
  if (!response.ok) return [];

  const data = await response.json() as CitiesResponse;
  return data.cities ?? [];
}

// ============================================================================
// Pure helpers
// ============================================================================

export function pickTariffForPoint(
  pointType: PickupPoint["type"],
  tariffs: TariffsGroup | null | undefined,
): TariffOption | null {
  if (!tariffs) return null;
  const all = tariffs.all ?? [];
  if (pointType === "POSTAMAT") return all.find((t) => t.tariffCode === 366) ?? tariffs.pvz ?? null;
  return all.find((t) => t.tariffCode === 136) ?? tariffs.pvz ?? null;
}

export function formatDays(min?: number, max?: number): string {
  if (!min || min < 1) return "";
  return min === max ? `${min} дн.` : `${min}–${max ?? min} дн.`;
}

export function formatPrice(value: number): string {
  return value > 0 ? `${Math.round(value).toLocaleString("ru-RU")} ₽` : "Бесплатно";
}
